import { Flex, Heading, HStack, VStack } from '@chakra-ui/layout';
import {
  useBreakpointValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Skeleton,
  TableContainer,
} from '@chakra-ui/react';
import React from 'react';
import { Pie, PieChart, Tooltip } from 'recharts';

import { Bank } from '@finley-technologies/finley-types';

import { FundingLenderSharesFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { colors } from 'packages/common-frontend/colors';
import { tableHeaderStyle } from 'packages/common-frontend/styles';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';

// TODO: @markhaghani handle many (10+) lenders case
const CIRCLE_SIZE = '.625rem';
const CIRCLE_PROPS = {
  borderRadius: '50%',
  marginRight: '0.5rem',
  height: CIRCLE_SIZE,
  width: CIRCLE_SIZE,
};

// constants
const COLORS = Object.values(colors.chart);
const TABLE_COLUMNS = [
  'Lender',
  'Lender Share',
  'Outstanding Balance',
  'Accrued Interest',
  'Projected Remaining Interest',
];

// types
type ChartDataSlice = {
  name: string;
  value: number;
  fill: string;
};

type PieChartProps = {
  data: ChartDataSlice[];
};

// fragments
const PieChartFragment = ({ data }: PieChartProps) => {
  const chartWidth = useBreakpointValue({ base: 200, lg: 240 });
  const chartHeight = useBreakpointValue({ base: 200, lg: 240 });
  const chartOuterRadius = useBreakpointValue({ base: 100, lg: 110 });

  return (
    <PieChart height={chartHeight} width={chartWidth}>
      <Pie
        cx="50%"
        cy="50%"
        data={data}
        dataKey="value"
        innerRadius={0}
        outerRadius={chartOuterRadius}
        style={{ outline: 'none' }} // disable blue outline when Piechart clicked
      />
      <Tooltip
        content={({ payload }) => {
          return (
            <Flex
              sx={{
                bgColor: colors.white,
                borderColor: colors.gray[100],
                borderRadius: 'md',
                borderWidth: '1px',
                color: colors.gray[600],
                p: '.5rem',
                whiteSpace: 'nowrap',
              }}
            >
              {typeof payload?.[0]?.value === 'number' &&
                `${payload?.[0]?.name}: ${percentageToPercentageLabel(
                  payload[0].value * 100,
                  6,
                  true,
                )}`}
            </Flex>
          );
        }}
      />
    </PieChart>
  );
};

const LenderSharesTableEmptyHeader = () => {
  return (
    <Tr>
      <Th sx={tableHeaderStyle}>Lender</Th>
      <Th sx={tableHeaderStyle}>Lender Share</Th>
      <Th sx={tableHeaderStyle}>Outstanding Balance</Th>
      <Th sx={tableHeaderStyle}>Accrued Interest</Th>
      <Th sx={tableHeaderStyle}>Projected Remaining Interest</Th>
    </Tr>
  );
};

const LenderSharesTableSkeletonRow = () => {
  return (
    <Tr>
      {Array.from({ length: 5 }).map((_, idx) => {
        const cellKey = idx;
        return (
          <Td key={cellKey}>
            <Skeleton h="50px" />
          </Td>
        );
      })}
    </Tr>
  );
};

export const LenderSharesTableSkeleton = () => {
  return (
    <TableContainer>
      <Table borderWidth="1px" borderColor={colors.gray[200]} my="1.5rem">
        <Thead>
          <LenderSharesTableEmptyHeader />
        </Thead>
        <Tbody>
          {Array.from({ length: 6 }).map((_, idx) => {
            const rowKey = idx;
            return <LenderSharesTableSkeletonRow key={rowKey} />;
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export const LenderSharesChart = ({
  lenderSharesData,
  totalOutstanding,
  loading,
}: {
  lenderSharesData: FundingLenderSharesFragmentGQLType[];
  totalOutstanding: number | null | undefined;
  loading: boolean;
}) => {
  const numberOfColumnsBreakpoint = useBreakpointValue({
    base: 1,
    lg: 1,
    xl: 2,
  });
  const chartDataFromLenderShares = lenderSharesData.map(
    (lenderShare, idx) => ({
      name: lenderShare.lenderOrganization.name,
      value: lenderShare.allocationPercent,
      fill: COLORS[idx],
    }),
  );
  return (
    <VStack
      sx={{
        alignItems: 'center',
        backgroundColor: colors.millet[50],
        borderColor: colors.gray[100],
        borderRadius: 'md',
        borderWidth: '1px',
        justifyContent: 'center',
        overflow: 'hidden',
        wrap: 'wrap',
        gap: '2rem',
        p: '3rem',
        w: '100%',
      }}
    >
      <HStack
        sx={{
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Heading size="md" fontWeight={600}>
          Current Lender Shares
        </Heading>
        {loading ? (
          <Skeleton h="2rem" w="10rem" />
        ) : (
          <Heading size="md" fontWeight={600}>
            {typeof totalOutstanding === 'number'
              ? formatCurrencyAmount(totalOutstanding, Bank.Currency.USD)
              : '-'}
          </Heading>
        )}
      </HStack>
      <Flex
        columnGap={numberOfColumnsBreakpoint === 2 ? '1rem' : 0}
        flexFlow={numberOfColumnsBreakpoint === 1 ? 'wrap' : 'nowrap'}
        gap={numberOfColumnsBreakpoint === 1 ? '1rem' : 0}
        sx={{
          w: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '2.5rem',
        }}
      >
        {loading ? (
          <Skeleton h="220px" w="280px" />
        ) : (
          <PieChartFragment data={chartDataFromLenderShares} />
        )}
        <Table variant="simple" size="sm">
          <Thead bg="transparent">
            <Tr>
              {TABLE_COLUMNS.map((columnName, idx) => (
                <Th
                  key={columnName}
                  textAlign={idx > 0 ? 'right' : 'left'}
                  sx={{ bg: 'transparent', textColor: 'black' }}
                >
                  {columnName}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <LenderSharesTableSkeletonRow />
            ) : (
              lenderSharesData
                .sort((a, b) => b.allocationPercent - a.allocationPercent)
                .map((lenderShare, idx) => (
                  <Tr
                    key={lenderShare.lenderOrganization.id}
                    textColor={colors.gray[600]}
                  >
                    <Td
                      sx={{
                        align: 'center',
                        textColor: 'black',
                        fontWeight: 500,
                      }}
                    >
                      <Flex align="center">
                        <span
                          style={{
                            backgroundColor: COLORS[idx],
                            ...CIRCLE_PROPS,
                            flexShrink: 0,
                          }}
                        />
                        {lenderShare.lenderOrganization.name}
                      </Flex>
                    </Td>
                    <Td textAlign="right">
                      {percentageToPercentageLabel(
                        lenderShare.allocationPercent * 100,
                        6,
                        true,
                      )}
                    </Td>
                    <Td textAlign="right">
                      {typeof lenderShare.outstandingBalance === 'number'
                        ? formatCurrencyAmount(
                            lenderShare.outstandingBalance,
                            Bank.Currency.USD,
                          )
                        : '-'}
                    </Td>
                    <Td textAlign="right">
                      {typeof lenderShare.totalAccruedInterest === 'number'
                        ? formatCurrencyAmount(
                            lenderShare.totalAccruedInterest,
                            Bank.Currency.USD,
                          )
                        : '-'}
                    </Td>
                    <Td textAlign="right">
                      {typeof lenderShare.projectedInterest === 'number'
                        ? formatCurrencyAmount(
                            lenderShare.projectedInterest,
                            Bank.Currency.USD,
                          )
                        : '-'}
                    </Td>
                  </Tr>
                ))
            )}
          </Tbody>
        </Table>
      </Flex>
    </VStack>
  );
};
